import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('crm_token');
    if (!token) { setLoading(false); return; }
    try {
      const res = await authAPI.getMe();
      setUser(res.data.data);
    } catch {
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('crm_token', token);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    setUser(null);
  };

  const updateUser = (data) => setUser(prev => ({ ...prev, ...data }));

  const isSuperAdmin = user?.role === 'superadmin';
  const isTenantAdmin = user?.role === 'tenant_admin';
  const isOwner = user?.role === 'owner';
  const isManager = user?.role === 'manager';
  const isStaff = user?.role === 'staff';
  const isGuest = user?.role === 'guest';
  const isAdmin = isSuperAdmin || isTenantAdmin;
  const canManage = isSuperAdmin || isTenantAdmin || isOwner || isManager;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser, loadUser, isSuperAdmin, isTenantAdmin, isOwner, isManager, isStaff, isGuest, isAdmin, canManage }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
